import type { StaticNavigationNavigationItemDtoType } from '@ori/static-api-client-react';
import type { FC, PropsWithChildren } from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { useFetchNavigationItems } from '../../../hooks/useFetchNavigationItems';
import type { NavigationItemsContext } from './types';

export const createNavigationItemsContext = <T extends StaticNavigationNavigationItemDtoType>(type: T) => {
  const navigationItemsContext = createContext<NavigationItemsContext | undefined>(undefined);
  const { Provider } = navigationItemsContext;
  const NavigationItemsProvider: FC<PropsWithChildren> = ({ children }) => {
    const [items, setItems] = useState<NavigationItemsContext['items']>([]);
    const fetchNavigationItems = useFetchNavigationItems();
    const fetch = useCallback(() => {
      // eslint-disable-next-line no-console -- log error to console
      fetchNavigationItems(type).then(setItems).catch(console.error);
    }, [fetchNavigationItems]);
    const value = useMemo(() => ({ items, fetch }), [fetch, items]);

    return <Provider value={value}>{children}</Provider>;
  };
  NavigationItemsProvider.displayName = `${type}Provider`;
  const useNavigationItems = () => {
    const context = useContext(navigationItemsContext);
    if (context) {
      return context;
    }
    throw new Error(`use${type} must be used within a ${type}Provider`);
  };

  return { navigationItemsContext, NavigationItemsProvider, useNavigationItems };
};
